import { StateCreator } from 'zustand';
import { MESSAGE_IDS } from '@trust_network/shared/message-ids';
import { fetchDelegationState } from '../../../entities/delegation/api/delegation-api';
import { DelegationSlice } from '../../../entities/delegation/model/types';
import { setStoredToken } from '../../../entities/user/model/session-storage';
import { UserSlice } from '../../../entities/user/model/types';
import { translateMessageId } from '../../../shared/i18n';
import { getApiActionErrorResult } from '../../../shared/api/lib/error';
import { LoadingState } from '../../../shared/store/model/types';
import { NotificationSlice } from '../../notifications/model/types';
import { AuthFieldErrors, AuthSlice } from './types';
import {
  loginWithPasswordApi,
  recoverPasswordApi,
  registerWithPasswordApi,
  requestPasswordOperationCodeApi,
  requestPhoneCodeApi,
  verifyPhoneCodeApi,
} from '../api/auth-api';

type AuthStoreState = AuthSlice &
  NotificationSlice &
  Pick<UserSlice, 'token' | 'user'> &
  Pick<DelegationSlice, 'chain' | 'root'> & {
    loading: LoadingState;
  };

export const createAuthSlice: StateCreator<
  AuthStoreState,
  [],
  [],
  AuthSlice
> = (set, get) => ({
  requestPasswordOperationCode: async (phone, operation) => {
    if (!phone.trim()) {
      return {
        success: false,
        fieldErrors: {
          phone: translateMessageId(MESSAGE_IDS.validationPhoneRequired),
        },
      };
    }

    set((state) => ({
      loading: { ...state.loading, auth: true },
    }));

    try {
      await requestPasswordOperationCodeApi(phone, operation);

      set((state) => ({
        loading: { ...state.loading, auth: false },
      }));
      return { success: true };
    } catch (error) {
      set((state) => ({
        loading: { ...state.loading, auth: false },
      }));
      return getApiActionErrorResult<AuthFieldErrors>(
        error,
        translateMessageId
      );
    }
  },
  loginWithPassword: async (phone, password) => {
    if (!phone.trim()) {
      return {
        success: false,
        fieldErrors: {
          phone: translateMessageId(MESSAGE_IDS.validationPhoneRequired),
        },
      };
    }

    if (!password.trim()) {
      return {
        success: false,
        fieldErrors: {
          password: translateMessageId(MESSAGE_IDS.validationError),
        },
      };
    }

    set((state) => ({
      loading: { ...state.loading, auth: true },
    }));

    try {
      const auth = await loginWithPasswordApi(phone, password);
      setStoredToken(auth.accessToken);
      const delegation = await fetchDelegationState(auth.accessToken);

      set((state) => ({
        token: auth.accessToken,
        user: auth.user,
        chain: delegation.chain,
        root: delegation.root,
        loading: { ...state.loading, auth: false },
      }));
      return { success: true };
    } catch (error) {
      set((state) => ({
        loading: { ...state.loading, auth: false },
      }));
      return getApiActionErrorResult<AuthFieldErrors>(
        error,
        translateMessageId
      );
    }
  },
  registerWithPassword: async (phone, code, password) => {
    if (!phone.trim()) {
      return {
        success: false,
        fieldErrors: {
          phone: translateMessageId(MESSAGE_IDS.validationPhoneRequired),
        },
      };
    }

    if (!code.trim()) {
      return {
        success: false,
        fieldErrors: {
          code: translateMessageId(MESSAGE_IDS.validationCodeRequired),
        },
      };
    }

    if (!password.trim()) {
      return {
        success: false,
        fieldErrors: {
          password: translateMessageId(MESSAGE_IDS.validationError),
        },
      };
    }

    set((state) => ({
      loading: { ...state.loading, auth: true },
    }));

    try {
      const auth = await registerWithPasswordApi(phone, code, password);
      setStoredToken(auth.accessToken);
      const delegation = await fetchDelegationState(auth.accessToken);

      set((state) => ({
        token: auth.accessToken,
        user: auth.user,
        chain: delegation.chain,
        root: delegation.root,
        loading: { ...state.loading, auth: false },
      }));
      return { success: true };
    } catch (error) {
      set((state) => ({
        loading: { ...state.loading, auth: false },
      }));
      return getApiActionErrorResult<AuthFieldErrors>(
        error,
        translateMessageId,
        {
          [MESSAGE_IDS.invalidCode]: 'code',
        }
      );
    }
  },
  recoverPassword: async (phone, code, newPassword) => {
    if (!phone.trim()) {
      return {
        success: false,
        fieldErrors: {
          phone: translateMessageId(MESSAGE_IDS.validationPhoneRequired),
        },
      };
    }

    if (!code.trim()) {
      return {
        success: false,
        fieldErrors: {
          code: translateMessageId(MESSAGE_IDS.validationCodeRequired),
        },
      };
    }

    if (!newPassword.trim()) {
      return {
        success: false,
        fieldErrors: {
          newPassword: translateMessageId(MESSAGE_IDS.validationError),
        },
      };
    }

    set((state) => ({
      loading: { ...state.loading, auth: true },
    }));

    try {
      const auth = await recoverPasswordApi(phone, code, newPassword);
      setStoredToken(auth.accessToken);
      const delegation = await fetchDelegationState(auth.accessToken);

      set((state) => ({
        token: auth.accessToken,
        user: auth.user,
        chain: delegation.chain,
        root: delegation.root,
        loading: { ...state.loading, auth: false },
      }));
      return { success: true };
    } catch (error) {
      set((state) => ({
        loading: { ...state.loading, auth: false },
      }));
      return getApiActionErrorResult<AuthFieldErrors>(
        error,
        translateMessageId,
        {
          [MESSAGE_IDS.invalidCode]: 'code',
        }
      );
    }
  },
  requestPhoneCode: async (phone) => {
    if (!phone.trim()) {
      return {
        success: false,
        fieldErrors: {
          phone: translateMessageId(MESSAGE_IDS.validationPhoneRequired),
        },
      };
    }

    set((state) => ({
      loading: { ...state.loading, auth: true },
    }));

    try {
      await requestPhoneCodeApi(phone);

      set((state) => ({
        loading: { ...state.loading, auth: false },
      }));
      return { success: true };
    } catch (error) {
      set((state) => ({
        loading: { ...state.loading, auth: false },
      }));
      return getApiActionErrorResult<AuthFieldErrors>(
        error,
        translateMessageId
      );
    }
  },
  verifyPhoneCode: async (phone, code) => {
    if (!phone.trim()) {
      return {
        success: false,
        fieldErrors: {
          phone: translateMessageId(MESSAGE_IDS.validationPhoneRequired),
        },
      };
    }

    if (!code.trim()) {
      return {
        success: false,
        fieldErrors: {
          code: translateMessageId(MESSAGE_IDS.validationCodeRequired),
        },
      };
    }

    if (code.trim().length !== 6) {
      return {
        success: false,
        fieldErrors: {
          code: translateMessageId(MESSAGE_IDS.validationCodeInvalid),
        },
      };
    }

    set((state) => ({
      loading: { ...state.loading, auth: true },
    }));

    try {
      const auth = await verifyPhoneCodeApi(phone, code);
      setStoredToken(auth.accessToken);
      const delegation = await fetchDelegationState(auth.accessToken);

      set((state) => ({
        token: auth.accessToken,
        user: auth.user,
        chain: delegation.chain,
        root: delegation.root,
        loading: { ...state.loading, auth: false },
      }));
      return { success: true };
    } catch (error) {
      set((state) => ({
        loading: { ...state.loading, auth: false },
      }));
      return getApiActionErrorResult<AuthFieldErrors>(
        error,
        translateMessageId,
        {
          [MESSAGE_IDS.invalidCode]: 'code',
        }
      );
    }
  },
});
